import { createFileRoute, Link } from "@tanstack/react-router";
import { HelpCircle, ArrowRight } from "lucide-react";
import { ANSWERS } from "@/data/answers";

const CANONICAL = "https://recoverytrauma.com/answers";

export const Route = createFileRoute("/answers/")({
  head: () => ({
    meta: [
      { title: "Trauma Recovery Questions, Answered | Recovery Trauma™" },
      {
        name: "description",
        content:
          "Direct answers to the questions people ask most about trauma recovery — flashbacks, dissociation, trauma bonds, the freeze response, grounding with CPTSD and journaling.",
      },
      { property: "og:title", content: "Trauma Recovery Questions, Answered — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Plain, trauma-informed answers to common questions about flashbacks, dissociation, trauma bonds, freeze, grounding and journaling.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: AnswersIndexPage,
});

function AnswersIndexPage() {
  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:py-12">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--gold-deep)]">
        Questions answered
      </p>
      <h1 className="mt-2 font-display text-2xl leading-tight text-foreground sm:text-3xl">
        Trauma recovery questions, answered plainly
      </h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
        Each page answers one question directly, then points to the fuller
        article and to the exercise that practises it. These pages are
        educational. They don&rsquo;t diagnose anything, and no technique is
        promised to work for everyone.
      </p>

      <ul className="mt-7 space-y-3">
        {ANSWERS.map((a) => (
          <li key={a.slug}>
            <Link
              to="/answers/$slug"
              params={{ slug: a.slug }}
              className="flex items-start gap-3 rounded-3xl border border-border/70 bg-card px-4 py-4 transition-colors hover:border-gold/50"
            >
              <span
                aria-hidden
                className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--gold-light)]"
              >
                <HelpCircle className="h-4.5 w-4.5 text-[var(--gold-deep)]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-display text-base leading-snug text-foreground">
                  {a.question}
                </span>
                <span className="mt-1.5 block text-[13px] leading-relaxed text-muted-foreground">
                  {a.shortAnswer}
                </span>
              </span>
              <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
            </Link>
          </li>
        ))}
      </ul>

      <p className="mt-8 rounded-2xl border border-border/60 bg-background/60 px-4 py-3 text-[12px] leading-relaxed text-muted-foreground">
        Recovery Trauma&trade; is an educational wellbeing platform. It is not
        therapy, medical treatment, diagnosis or emergency support. If you are
        in danger or in crisis, please use the{" "}
        <Link to="/crisis" className="underline underline-offset-2">
          Crisis Support page
        </Link>{" "}
        or your local emergency services.
      </p>
    </div>
  );
}
